/**
 * Bottom Navigation Bar Component for Mobile
 * 
 * Provides a persistent iOS-style floating bottom navigation bar.
 * Features:
 * - Floating pill design with glass effect
 * - 3 navigation items: Dashboard, Stocks, Account
 * - Active state highlighting
 * - Profile dropdown menu
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Building, Settings, Bug } from 'lucide-react';

export function BottomNavigation() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Navigation items configuration - using emojis!
  const navigationItems = [
    { path: '/dashboard', label: 'Dashboard', exact: true, emoji: '🍆' },
    { path: '/ai-analyst', label: 'AI', emoji: '🧠' },
    { path: '/news', label: 'News', emoji: '🕵️' },
    { path: '/webhook-stocks', label: 'Stocks', emoji: '🤖' },
  ];

  // Check if a navigation item is active
  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div 
      className="sm:hidden fixed left-1/2 -translate-x-1/2 z-50"
      style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      {/* Profile Dropdown Menu */}
      {isProfileDropdownOpen && (
        <div 
          ref={dropdownRef}
          className="absolute bottom-full right-0 mb-2 w-52 bg-white/70 dark:bg-gray-900/70 backdrop-blur-2xl rounded-2xl shadow-lg py-2 border border-white/20 dark:border-white/10"
        >
          <Link
            to="/positions"
            className="flex items-center w-full px-5 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => setIsProfileDropdownOpen(false)}
          >
            <Building className="h-4 w-4 mr-3" />
            Brokerages
          </Link>
          <Link
            to="/settings"
            className="flex items-center w-full px-5 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => setIsProfileDropdownOpen(false)}
          >
            <Settings className="h-4 w-4 mr-3" />
            Settings
          </Link>
          <Link
            to="/debug"
            className="flex items-center w-full px-5 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => setIsProfileDropdownOpen(false)}
          >
            <Bug className="h-4 w-4 mr-3" />
            Debug
          </Link>
          <div className="border-t border-gray-200/50 dark:border-gray-600/50 my-2"></div>
          <button
            onClick={() => {
              logout();
              setIsProfileDropdownOpen(false);
            }}
            className="block w-full text-left px-5 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
          >
            Logout
          </button>
        </div>
      )}

      {/* Floating Bottom Navigation Bar */}
      <nav 
        className="bg-white/60 dark:bg-gray-900/70 backdrop-blur-xl backdrop-saturate-150 rounded-full shadow-lg border border-gray-200/50 dark:border-white/10 px-2 py-2 h-[60px]"
      >
        <div className="flex justify-around items-center gap-1 h-full">
          {/* Main navigation items */}
          {navigationItems.map((item) => {
            const active = isActive(item.path, item.exact);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center px-5 py-1.5 rounded-full transition-colors"
              >
                <div className="h-7 flex items-center justify-center">
                  <span className={`text-2xl leading-none ${active ? 'grayscale-0' : 'grayscale opacity-60'} transition-all`}>
                    {item.emoji}
                  </span>
                </div>
                <span 
                  className={`text-[10px] mt-1 ${
                    active 
                      ? 'text-blue-600 dark:text-blue-400 font-medium' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* Profile Icon */}
          <button
            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            className="flex flex-col items-center justify-center px-5 py-1.5 rounded-full transition-colors"
          >
            <div className="h-7 flex items-center justify-center">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                isProfileDropdownOpen
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}>
                {user?.firstName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>
            <span 
              className={`text-[10px] mt-1 ${
                isProfileDropdownOpen
                  ? 'text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Account
            </span>
          </button>
        </div>
      </nav>
    </div>
  );
}
